import { FilterOutlined, ReloadOutlined, MoreOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Form, Input, Space, Table, Typography, Modal, message, Tag, Dropdown } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import { useContext, useState } from 'react';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { Retailer } from '../../types/retailers.types';
import { handleAPIError } from '../../utils/helpers';
import CopyableText from '../../components/copyable-text';
import { UserMinimal } from '../../types/user.types';
import { AiOutlineUsergroupAdd } from 'react-icons/ai';
import { MdAccountBalance } from 'react-icons/md';
import dayjs from 'dayjs';
import CreateRetailer from '../../components/create-retailer';
import CreateRetailerPaymentAccount from '../../components/create-retailer-payment-account';
import RetailerPaymentAccounts from '../../components/retailer-payment-accounts';
import { AuthContext } from '../../context/auth.context';

interface FilterValues {
	vendor_id: string | null;
}

const Retailers = () => {
	const { user } = useContext(AuthContext);
	const queryClient = useQueryClient();
	const [form] = Form.useForm<FilterValues>();
	const [paginationConfig, setPaginationConfig] = useState({
		page: 1,
		pageSize: 10,
	});
	const [count, setCount] = useState(0);
	const [filters, setFilters] = useState<FilterValues>({
		vendor_id: null,
	});

	const [showCreateRetailerModal, setShowCreateRetailerModal] = useState(false);
	const [showPaymentAccountsModal, setShowPaymentAccountsModal] = useState(false);
	const [showCreatePaymentAccountModal, setShowCreatePaymentAccountModal] = useState(false);
	const [selectedRetailer, setSelectedRetailer] = useState<Retailer | null>(null);

	const { data, error, isLoading } = useQuery({
		queryKey: ['retailers', paginationConfig.page, paginationConfig.pageSize, filters],
		queryFn: async () => {
			const params = {
				page: paginationConfig.page,
				limit: paginationConfig.pageSize,
				vendor_id: filters.vendor_id,
			};

			const response = await axios.get('/admin/retailers', {
				withCredentials: true,
				params,
			});
			setCount(response.data.count);
			return response.data;
		},
	});

	const { mutate: toggleRetailerStatus, isPending: isToggling } = useMutation({
		mutationFn: async ({ id, activate }: { id: number; activate: boolean }) => {
			const endpoint = activate ? `/admin/retailers/${id}/activate` : `/admin/retailers/${id}/deactivate`;

			const response = await axios.patch(
				endpoint,
				{},
				{
					withCredentials: true,
				},
			);
			return response.data;
		},
		onSuccess: () => {
			message.success('Retailer status updated successfully');
			queryClient.invalidateQueries({ queryKey: ['retailers'] });
		},
		onError: (error) => {
			handleAPIError(error);
		},
	});

	const showToggleConfirm = (id: number, is_active: boolean) => {
		const action = is_active ? 'deactivate' : 'activate';
		Modal.confirm({
			title: `Are you sure you want to ${action} this retailer?`,
			content: `This will ${action} the retailer's account.`,
			okText: `Yes, ${action}`,
			okType: is_active ? 'danger' : 'primary',
			cancelText: 'Cancel',
			onOk() {
				return toggleRetailerStatus({ id, activate: !is_active });
			},
		});
	};

	const handleViewPaymentAccounts = (retailer: Retailer) => {
		setSelectedRetailer(retailer);
		setShowPaymentAccountsModal(true);
	};

	const handleCreatePaymentAccount = (retailer: Retailer) => {
		setSelectedRetailer(retailer);
		setShowCreatePaymentAccountModal(true);
	};

	const handleCreatePaymentAccountFromView = () => {
		// Close the payment accounts modal and open the create modal
		setShowPaymentAccountsModal(false);
		setShowCreatePaymentAccountModal(true);
	};

	const { mutate: deleteRetailer, isPending: isDeleting } = useMutation({
		mutationFn: async (id: number) => {
			const response = await axios.delete(`/admin/retailers/${id}/delete`, {
				withCredentials: true,
			});
			return response.data;
		},
		onSuccess: (data) => {
			message.success(data?.msg ?? 'Retailer deleted');
			queryClient.invalidateQueries({ queryKey: ['retailers'] });
		},
		onError: handleAPIError,
	});

	const showDeleteConfirm = (record: Retailer) => {
		Modal.confirm({
			title: `Delete ${record.business_name}?`,
			content:
				'This cannot be undone, and their login is deactivated with it. A retailer with devices, payouts or payment accounts against it will be refused.',
			okText: 'Delete',
			okButtonProps: { danger: true },
			onOk: () => deleteRetailer(record.id),
		});
	};

	const getActionMenuItems = (record: Retailer): MenuProps['items'] => {
		const items: MenuProps['items'] = [
			{
				key: 'payment-accounts',
				label: 'View Payment Accounts',
				onClick: () => handleViewPaymentAccounts(record),
			},
			{
				key: 'create-payment-account',
				label: 'Create Payment Account',
				onClick: () => handleCreatePaymentAccount(record),
			},
		];

		// Add activate/deactivate option if user is super admin
		if (user?.is_superuser) {
			items.push({
				type: 'divider',
			});
			items.push({
				key: 'toggle-status',
				label: record.is_active ? 'Deactivate' : 'Activate',
				onClick: () => showToggleConfirm(record.id, record.is_active),
				danger: record.is_active,
			});
			items.push({
				key: 'delete-retailer',
				label: 'Delete Retailer',
				onClick: () => showDeleteConfirm(record),
				danger: true,
			});
		}

		return items;
	};

	if (error) {
		handleAPIError(error);
	}

	const retailers: Retailer[] = data?.results || [];

	const columns: ColumnsType<Retailer> = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: 'Name',
			dataIndex: 'business_name',
			key: 'business_name',
		},
		{
			title: 'Vendor ID',
			dataIndex: 'vendor_id',
			key: 'vendor_id',
			render: (vendor_id: string) => <CopyableText text={vendor_id} />,
		},
		{
			title: 'Address',
			dataIndex: 'district',
			key: 'district',
			render: (_, record: Retailer) => {
				return (
					<Card
						size="small"
						className="border-dashed shadow-sm transition-shadow hover:shadow-md"
					>
						<div className="space-y-1">
							<Typography.Text strong>District:</Typography.Text> <Typography.Text>{record.district}</Typography.Text>
						</div>
						<div className="space-y-1">
							<Typography.Text strong>Town:</Typography.Text> <Typography.Text>{record.town}</Typography.Text>
						</div>
						<div className="space-y-1">
							<Typography.Text strong>Shop Number:</Typography.Text> <Typography.Text>{record.shop_number}</Typography.Text>
						</div>
					</Card>
				);
			},
		},
		{
			title: 'User Details',
			dataIndex: 'user',
			key: 'user',
			render: (user: UserMinimal) => {
				return (
					<Card
						size="small"
						className="border-dashed shadow-sm transition-shadow hover:shadow-md"
					>
						<div className="space-y-1">
							<Typography.Text strong>Name:</Typography.Text>{' '}
							<Typography.Text>
								{user.first_name} {user.last_name}
							</Typography.Text>
						</div>
						<div className="space-y-1">
							<Typography.Text strong>Phone Number:</Typography.Text> <Typography.Text>{user.phone_number}</Typography.Text>
						</div>
					</Card>
				);
			},
		},
		{
			title: 'Date',
			dataIndex: 'created_at',
			key: 'created_at',
			render: (created_at: Date) => {
				return <Typography.Text>{dayjs(created_at).format('DD MMM YYYY - LT')}</Typography.Text>;
			},
		},
		{
			title: 'Status',
			dataIndex: 'is_active',
			key: 'is_active',
			render: (is_active: boolean) => {
				return <Tag color={is_active ? 'green' : 'red'}>{is_active ? 'Active' : 'Inactive'}</Tag>;
			},
		},
	];

	// Add the Actions column - always show for all users (payment accounts), conditionally show other actions
	columns.push({
		title: 'Actions',
		key: 'actions',
		render: (_, record: Retailer) => (
			<Dropdown
				menu={{ items: getActionMenuItems(record) }}
				trigger={['click']}
				placement="bottomRight"
			>
				<Button
					type="text"
					icon={<MoreOutlined />}
					loading={isToggling || isDeleting}
				/>
			</Dropdown>
		),
	});

	const handleFilterApply = (values: FilterValues) => {
		setFilters(values);
		setPaginationConfig({
			...paginationConfig,
			page: 1, // Reset to first page when filtering
		});
	};

	const handleFilterReset = () => {
		form.resetFields();
		setFilters({
			vendor_id: null,
		});
		setPaginationConfig({
			...paginationConfig,
			page: 1,
		});
	};

	return (
		<AppLayout
			title="Retailers"
			subtitle="View and manage all retailers."
		>
			<Card className="mb-4 shadow-sm">
				<Form
					form={form}
					layout="horizontal"
					onFinish={handleFilterApply}
					initialValues={filters}
				>
					<div className="flex flex-wrap items-end gap-4">
						<Form.Item
							name="vendor_id"
							label="Vendor ID"
							className="mb-0 min-w-[150px]"
						>
							<Input
								placeholder="Vendor ID"
								allowClear
							/>
						</Form.Item>

						<Form.Item className="mb-0 flex-shrink-0">
							<Space>
								<Button
									type="primary"
									htmlType="submit"
									icon={<FilterOutlined />}
								>
									Apply Filters
								</Button>
								<Button
									onClick={handleFilterReset}
									icon={<ReloadOutlined />}
								>
									Reset
								</Button>
							</Space>
						</Form.Item>

						<div className="ml-auto flex gap-2">
							<Button
								icon={<MdAccountBalance size={20} />}
								onClick={() => setShowCreatePaymentAccountModal(true)}
							>
								Create Payment Account
							</Button>
							<Button
								icon={<AiOutlineUsergroupAdd size={20} />}
								type="primary"
								onClick={() => setShowCreateRetailerModal(true)}
							>
								Add Retailer
							</Button>
						</div>
					</div>
				</Form>
			</Card>

			<div className="mb-20">
				<Table
					columns={columns}
					dataSource={retailers}
					rowKey="id"
					pagination={{
						total: count,
						pageSize: paginationConfig.pageSize,
						current: paginationConfig.page,
						showSizeChanger: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
						onChange: (page, pageSize) => {
							setPaginationConfig({ page, pageSize });
						},
					}}
					loading={isLoading}
				/>
			</div>
			{showCreateRetailerModal && (
				<CreateRetailer
					open={showCreateRetailerModal}
					onClose={() => setShowCreateRetailerModal(false)}
				/>
			)}
			{showPaymentAccountsModal && selectedRetailer && (
				<RetailerPaymentAccounts
					open={showPaymentAccountsModal}
					onClose={() => {
						setShowPaymentAccountsModal(false);
						setSelectedRetailer(null);
					}}
					retailerId={selectedRetailer.id}
					retailerName={selectedRetailer.business_name}
					onCreatePaymentAccount={handleCreatePaymentAccountFromView}
				/>
			)}
			{showCreatePaymentAccountModal && (
				<CreateRetailerPaymentAccount
					open={showCreatePaymentAccountModal}
					onClose={() => {
						setShowCreatePaymentAccountModal(false);
						setSelectedRetailer(null);
					}}
					preSelectedRetailerId={selectedRetailer?.id}
				/>
			)}
		</AppLayout>
	);
};

export default Retailers;
